import { GridColDef, GridRowSelectionModel } from '@mui/x-data-grid/models';
import { useCallback, useMemo, useState } from 'react';
import { OrderSummary } from 'api/models/Orders';
import theme from 'App.theme';
import Button from 'components/Button';
import { NamedSelectOption } from 'components/DropDown/ValidatingSelect';
import SearchInput from 'components/SearchInput';
import { StyledDataGrid } from 'components/StyledDataGrid/StyledDataGrid';
import { useLanguage } from 'context/AppContext';
import { replaceTimeInfoWithUtcInDate } from 'utils/helpers/DateTimeFormat.helpers';

type RowProps = {
  id: string;
  surgeryReference: string;
  orderNumber: string;
  supplierName: string;
  surgeonName: string;
  surgeryDate: string;
  surgeryDescription: string;
  patientInitials: string;
};

type Props = {
  orders: OrderSummary[];
  supplierOptions: NamedSelectOption[];
  onContinue: (orderId?: string) => void;
};

const SurgerySelect = ({ orders, supplierOptions, onContinue }: Props) => {
  const lang = useLanguage()[0].ReceiveDeliveryDialog;

  const [searchTerm, setSearchTerm] = useState('');

  const formatSurgeryDate = useCallback(
    (surgeryDate: string | Date) => replaceTimeInfoWithUtcInDate(surgeryDate).split('T')[0],
    [],
  );

  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);

  const onRowSelectionModelChange = useCallback(
    (selection: GridRowSelectionModel) => {
      setRowSelectionModel(selection[0] == rowSelectionModel[0] ? [] : selection);
    },
    [rowSelectionModel],
  );

  const selectedOrderRecordId = rowSelectionModel[0] as string | undefined;

  const surgeries = useMemo(() => orders.filter(order => order.surgeryReference || !order.supplierCreated), [orders]);

  const memoizedRows = useMemo(() => {
    const searchTermLc = searchTerm.toLowerCase();
    let selectedOrderRecordIdIsDisplayed = false;

    const rows = surgeries.reduce<RowProps[]>((acc, order) => {
      const supplierName = supplierOptions.find(s => s.id == order.tenantId)?.label || '';
      const surgeryReferenceLc = order.surgeryReference?.toLowerCase() || '';
      const patientInitialsLc = order.patientInitials?.toLowerCase() || '';
      const descLc = order.surgeryDescription?.toLowerCase() || '';
      const commentsLc = order.surgeryComments?.toLowerCase() || '';

      const searchMatches =
        !searchTerm ||
        surgeryReferenceLc.includes(searchTermLc) ||
        patientInitialsLc.includes(searchTermLc) ||
        descLc.includes(searchTermLc) ||
        commentsLc.includes(searchTermLc);

      if (searchMatches) {
        acc.push({
          id: order.orderRecordId,
          surgeryReference: order.surgeryReference || '',
          orderNumber: order.orderNumber,
          supplierName,
          surgeonName: order.surgeonName,
          surgeryDate: formatSurgeryDate(order.surgeryDate),
          surgeryDescription: order.surgeryDescription || '',
          patientInitials: order.patientInitials || '',
        });

        if (selectedOrderRecordId == order.orderRecordId) {
          selectedOrderRecordIdIsDisplayed = true;
        }
      }

      return acc;
    }, []);

    if (selectedOrderRecordId && !selectedOrderRecordIdIsDisplayed) {
      setRowSelectionModel([]);
    }

    return rows;
  }, [formatSurgeryDate, surgeries, searchTerm, selectedOrderRecordId, supplierOptions]);

  const memoizedColumns = useMemo(() => {
    const colKeys: (keyof RowProps)[] = [
      'surgeryReference',
      'orderNumber',
      'supplierName',
      'surgeonName',
      'surgeryDate',
      'patientInitials',
    ];
    const columns: GridColDef[] = colKeys.map((key, idx) => ({
      field: key,
      headerName: lang.ordersTable[key],
      align: 'center',
      headerAlign: 'center',
      minWidth: 120,
      sortable: !idx,
    }));
    return columns;
  }, [lang.ordersTable]);

  const handleContinue = useCallback(
    (orderRecordId?: string) => {
      onContinue(orderRecordId);
    },
    [onContinue],
  );

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        marginTop: theme.layout.spacing.medium,
        gap: theme.layout.spacing.medium,
      }}
    >
      {surgeries.length > 3 && <SearchInput onChange={setSearchTerm} />}

      <StyledDataGrid
        sx={{ maxHeight: '308px' }}
        hidePagination
        pagination
        disableColumnMenu
        columns={memoizedColumns}
        rows={memoizedRows}
        rowHeight={theme.sizes.rowHeight}
        rowSelectionModel={rowSelectionModel}
        onRowSelectionModelChange={onRowSelectionModelChange}
        getRowId={row => row.id}
      />

      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          gap: theme.layout.spacing.medium,
        }}
      >
        <Button
          label={lang.createNewSurgery}
          colors="active on white"
          onClick={() => handleContinue()}
          disabled={!!selectedOrderRecordId}
          sx={{ height: '40px', flex: 1 }}
        />
        <Button
          label={selectedOrderRecordId ? lang.attachToSelectedSurgery : lang.selectSurgeryAbove}
          colors="white on active"
          onClick={() => handleContinue(selectedOrderRecordId)}
          disabled={!selectedOrderRecordId}
          sx={{ height: '40px', flex: 1 }}
        />
      </div>
    </div>
  );
};

export default SurgerySelect;
